import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ShieldCheck,
  XCircle,
  Lock,
  ArrowRight,
  Clock,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const moneyFmt = (n, currency = "INR") =>
  n == null
    ? "—"
    : new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        maximumFractionDigits: 0,
      }).format(n);

function DecideDialog({ open, onOpenChange, item, asUserId, onDone }) {
  const [decision, setDecision] = useState("approved");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setDecision("approved");
      setComment("");
    }
  }, [open]);

  const submit = async () => {
    if (!asUserId) {
      toast.error("Pick a 'view as' user first.");
      return;
    }
    setBusy(true);
    try {
      await v2Api.approvals.decide(
        item.step_id,
        { decision, comment: comment || null },
        asUserId,
      );
      toast.success(`Step ${item.step_no} ${decision}`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Could not record decision");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {decision === "approved" ? "Approve" : "Reject"} {item?.entity_label}
          </DialogTitle>
          <DialogDescription>
            Step {item?.step_no} ({item?.required_role_code}) on chain{" "}
            <span className="font-mono text-[11px]">{item?.chain_id?.slice(0, 8)}</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              variant={decision === "approved" ? "default" : "outline"}
              onClick={() => setDecision("approved")}
              size="sm"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button
              variant={decision === "rejected" ? "destructive" : "outline"}
              onClick={() => setDecision("rejected")}
              size="sm"
            >
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
          </div>
          <div>
            <Label>Comment</Label>
            <Textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                decision === "approved"
                  ? "Optional — context for the next approver"
                  : "Reason for rejection (recommended)"
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            variant={decision === "rejected" ? "destructive" : "default"}
            disabled={busy}
          >
            {busy ? "Recording…" : `Confirm ${decision}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InboxItem({ item, asUserId, onRefresh }) {
  const [openDecide, setOpenDecide] = useState(false);
  return (
    <Card className="hover:border-blue-300 transition">
      <CardContent className="py-4 px-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant={item.is_unlocked ? "default" : "secondary"}
                className={`text-[11px] uppercase ${
                  item.is_unlocked
                    ? "bg-blue-100 text-blue-800 border-blue-200"
                    : "bg-slate-100 text-slate-600 border-slate-200"
                }`}
              >
                {item.is_unlocked ? "Your turn" : (
                  <>
                    <Lock className="w-3 h-3 inline mr-1" />
                    Waiting on {item.waiting_on?.join(", ")}
                  </>
                )}
              </Badge>
              <Badge variant="outline" className="text-[11px]">
                Step {item.step_no} · {item.required_role_code}
              </Badge>
              <Badge variant="outline" className="text-[11px] uppercase">
                {item.entity_type}
              </Badge>
              <Badge variant="outline" className="text-[11px]">
                {item.chain_mode}
              </Badge>
            </div>
            <p className="font-medium text-slate-900 mt-2">
              {item.entity_label}
              {item.entity_summary?.buyer_name && (
                <span className="text-slate-500 font-normal ml-2 text-sm">
                  for {item.entity_summary.buyer_name}
                </span>
              )}
            </p>
            {item.project_name && (
              <Link
                to={`/v2/projects/${item.project_id}`}
                className="text-xs text-blue-600 hover:underline mt-0.5 inline-block"
              >
                {item.project_code} · {item.project_name}
              </Link>
            )}
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {item.initiated_at?.slice(0, 16)}
              </span>
              {item.initiated_by_user_name && (
                <span>by {item.initiated_by_user_name}</span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            {item.entity_type === "quotation" && item.entity_summary && (
              <>
                <p className="font-mono font-semibold text-blue-700 text-lg">
                  {moneyFmt(item.entity_summary.grand_total, item.entity_summary.currency)}
                </p>
                <p className="text-[11px] text-slate-500">
                  Sub {moneyFmt(item.entity_summary.subtotal, item.entity_summary.currency)}{" "}
                  · GST{" "}
                  {moneyFmt(
                    (item.entity_summary.cgst_total || 0) +
                      (item.entity_summary.sgst_total || 0) +
                      (item.entity_summary.igst_total || 0),
                    item.entity_summary.currency,
                  )}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            size="sm"
            disabled={!item.is_unlocked}
            onClick={() => setOpenDecide(true)}
          >
            <ShieldCheck className="w-4 h-4 mr-1" /> Decide
          </Button>
          {item.project_id && (
            <Link to={`/v2/projects/${item.project_id}`}>
              <Button size="sm" variant="outline">
                Open project <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          )}
        </div>
      </CardContent>

      <DecideDialog
        open={openDecide}
        onOpenChange={setOpenDecide}
        item={item}
        asUserId={asUserId}
        onDone={onRefresh}
      />
    </Card>
  );
}

const ROLE_OPTIONS = [
  { code: "admin",   label: "Admin" },
  { code: "manager", label: "Manager" },
  { code: "member",  label: "Member" },
];

export default function ApprovalsInbox() {
  const [roleCode, setRoleCode] = useState("admin");
  const [includeLocked, setIncludeLocked] = useState(false);
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [asUserId, setAsUserId] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [data, u] = await Promise.all([
        v2Api.approvals.inbox(roleCode, includeLocked),
        users.length ? Promise.resolve(users) : v2Api.users(),
      ]);
      setItems(data);
      if (!users.length) setUsers(u);
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Could not load inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleCode, includeLocked]);

  // Auto-pick a user matching the chosen role for the "view as" selector.
  useEffect(() => {
    if (!users.length) return;
    const want = roleCode;
    const fallbackByRole = {
      admin:   ["admin@axisindia.in", "ceo@axisindia.in"],
      manager: ["head.tech@axisindia.in", "head.fin@axisindia.in"],
      member:  ["ba1@axisindia.in", "tech1@axisindia.in"],
    };
    const candidates = fallbackByRole[want] || [];
    const picked = users.find(
      (u) => u.legacy_role === want || candidates.includes(u.email),
    );
    if (picked) setAsUserId(picked.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, roleCode]);

  const grouped = useMemo(() => {
    const yours = items.filter((i) => i.is_unlocked);
    const waiting = items.filter((i) => !i.is_unlocked);
    return { yours, waiting };
  }, [items]);

  return (
    <div className="px-8 py-6 max-w-[1100px] mx-auto">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Approvals Inbox
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Pending steps for the selected role. Sequential chains show only the
            current step as <span className="font-medium">Your turn</span>;
            earlier steps' approvers gate progression.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={refresh}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />{" "}
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div>
          <Label className="text-xs">View role</Label>
          <Select value={roleCode} onValueChange={setRoleCode}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((r) => (
                <SelectItem key={r.code} value={r.code}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Acting as user</Label>
          <Select value={asUserId} onValueChange={setAsUserId}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a user" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name} — {u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeLocked}
              onChange={(e) => setIncludeLocked(e.target.checked)}
              className="h-4 w-4"
            />
            Show steps not yet unlocked
          </label>
        </div>
      </div>

      <section className="mb-7">
        <h2 className="text-sm font-semibold text-slate-700 mb-2">
          Your turn ({grouped.yours.length})
        </h2>
        {grouped.yours.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-10 text-center text-slate-500 text-sm">
              No pending decisions for{" "}
              <span className="font-medium">{roleCode}</span>. Generate a
              quotation and click <span className="font-medium">Send for approval</span>{" "}
              from a project to see the queue light up.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {grouped.yours.map((it) => (
              <InboxItem
                key={it.step_id}
                item={it}
                asUserId={asUserId}
                onRefresh={refresh}
              />
            ))}
          </div>
        )}
      </section>

      {includeLocked && grouped.waiting.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-500 mb-2">
            Waiting upstream ({grouped.waiting.length})
          </h2>
          <div className="space-y-3 opacity-75">
            {grouped.waiting.map((it) => (
              <InboxItem
                key={it.step_id}
                item={it}
                asUserId={asUserId}
                onRefresh={refresh}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
